<i18n lang="yaml" src="@/locales/common.yaml"></i18n>

<i18n lang="yaml" src="@/locales/notifications.yaml"></i18n>

<template>
  <v-navigation-drawer v-model="show" :mini-variant="mini" color="nav-bar">
    <div class="logo-container pt-4 pb-2">
      <img
        :src="logoSrc"
        class="logo"
        :alt="siteLogo ? siteLogo.alt_text : 'CELUS'"
        id="logo-image"
      />
    </div>
    <!-- stuff that should be here on xs displays because it is hidden from the app-bar -->
    <OrganizationSelector
      :lang="appLanguage"
      internal-label
      v-if="$vuetify.display.smAndDown"
      class="pb-0"
    ></OrganizationSelector>
    <SelectedDateRangeWidget
      v-if="$vuetify.display.smAndDown"
      class="pl-0"
    ></SelectedDateRangeWidget>
    <v-divider class="d-md-none"></v-divider>
    <!-- the navigation menu itself -->
    <v-list
      class="pa-0"
      density="compact"
      v-for="(group, index) in activeGroups"
      :key="index"
      nav
      :lines="false"
      bg-color="nav-bar"
    >
      <v-list-subheader
        class="menu-subheader mt-3 on-nav-bar text-uppercase font-weight-bold opacity-60"
      >
        {{ group.title }}
      </v-list-subheader>
      <MenuListItem
        v-for="item in group.items.filter((itm) =>
          itm.show == null ? true : itm.show,
        )"
        :item="item"
        :key="item.title"
        :notifications="notifications"
        :chip="item.chip"
      >
      </MenuListItem>
    </v-list>
    <template #append>
      <div class="small text-center mb-2">
        <router-link :to="{ name: 'changelog' }" class="on-nav-bar">
          {{ $t("celus_version") }}: {{ celusVersion }}
        </router-link>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import SelectedDateRangeWidget from "@/components/SelectedDateRangeWidget";
import OrganizationSelector from "@/components/selectors/OrganizationSelector";
import MenuListItem from "@/components/util/MenuListItem";
import defaultLogoDark from "@/assets/celus-horizontal-dark.svg";
import defaultLogoLight from "@/assets/celus-horizontal-light.svg";
import { computed } from "vue";
import { mapGetters, mapState } from "vuex";
import { useTheme } from "vuetify";

export default {
  name: "SidePanel",
  components: { MenuListItem, SelectedDateRangeWidget, OrganizationSelector },
  props: {
    modelValue: { default: true, type: Boolean },
  },
  setup() {
    const theme = useTheme();
    // per-theme choice of the logo variant (`nav-logo-light` in plugins/vuetify.js)
    const navLogoLight = computed(
      () => theme.current.value.variables["nav-logo-light"] === true,
    );
    return { navLogoLight };
  },
  data() {
    return {
      mini: false,
      show: this.modelValue,
    };
  },
  computed: {
    ...mapState({
      user: "user",
      appLanguage: "appLanguage",
      siteLogo: (state) => state.siteConfig.siteLogo,
    }),
    ...mapGetters({
      organization: "selectedOrganization",
      showAdminStuff: "showAdminStuff",
      showManagementStuff: "showManagementStuff",
      notifications: "getNotifications",
      consortialInstall: "consortialInstall",
      celusVersion: "celusVersion",
      isRawImportEnabled: "isRawImportEnabled",
      allowUserManagement: "allowUserManagement",
      usesRegistryBackend: "usesRegistryBackend",
    }),
    isSuperuser() {
      return this.user && this.user.is_superuser;
    },
    logoSrc() {
      if (this.siteLogo) {
        return this.siteLogo.img;
      }
      // the "light" logo has white text, for a dark nav bar
      return this.navLogoLight ? defaultLogoLight : defaultLogoDark;
    },
    groups() {
      return [
        {
          title: this.$i18n.t("pages.content"),
          items: [
            {
              title: this.$i18n.t("pages.dashboard"),
              icon: this.$icons.dashboard,
              linkTo: "dashboard",
            },
            {
              title: this.$i18n.t("pages.organizations"),
              icon: this.$icons.organization,
              linkTo: "organization-list",
              show: this.showManagementStuff,
            },
            {
              title: this.$i18n.t("pages.platforms"),
              icon: this.$icons.list_box,
              linkTo: "platform-list",
            },
            {
              title: this.$i18n.t("pages.all_titles"),
              icon: this.$icons.copy,
              linkTo: "title-list",
            },
            {
              title: this.$i18n.t("pages.tags"),
              icon: this.$icons.tags,
              items: [
                {
                  title: this.$i18n.t("pages.tag_management"),
                  linkTo: "tags",
                },
                {
                  title: this.$i18n.t("pages.title_lists"),
                  linkTo: "tagging-batches",
                },
              ],
            },
            {
              title: this.$i18n.t("pages.interest_overview"),
              icon: this.$icons.flag,
              linkTo: "interest-overview",
            },
            {
              title: this.$i18n.t("pages.heatmap"),
              icon: this.$icons.map,
              linkTo: "heatmap",
              show: this.consortialInstall,
            },
            {
              title: this.$i18n.t("labels.annotations"),
              icon: this.$icons.pen,
              linkTo: "annotations",
            },
          ],
          show: true,
        },
        {
          title: this.$i18n.t("pages.analytics"),
          items: [
            {
              title: this.$i18n.t("pages.flexitable"),
              icon: this.$icons.chart_line,
              items: [
                {
                  title: this.$i18n.t("pages.create_report"),
                  linkTo: "flexitable",
                },
                {
                  title: this.$i18n.t("pages.flexi_reports"),
                  linkTo: "flexireports",
                },
                {
                  title: this.$t("pages.exports"),
                  linkTo: "exports",
                },
                {
                  title: this.$t("pages.specialized_reports"),
                  linkTo: "specialized-reports",
                },
              ],
            },
            {
              title: this.$t("pages.subscriptions"),
              icon: this.$icons.layers,
              items: [
                {
                  title: this.$i18n.t("pages.overlap_analysis_titles"),
                  linkTo: "overlap-analysis",
                },
                {
                  title: this.$i18n.t("pages.overlap_analysis_platforms"),
                  linkTo: "platform-overlap-analysis",
                },
                {
                  title: this.$i18n.t("pages.title_list_overlap"),
                  linkTo: "title-list-overlap",
                },
              ],
            },
            {
              title: this.$t("pages.anomaly_report"),
              icon: this.$icons.file_search,
              linkTo: "anomaly-report",
            },
            {
              title: this.$t("pages.ch_export"),
              icon: this.$icons.database,
              linkTo: "ch-export",
              show: this.showAdminStuff,
            },
          ],
          show: true,
        },
        {
          title: this.$i18n.t("pages.data_management"),
          items: [
            {
              title: this.$i18n.t("pages.sushi"),
              icon: this.$icons.cloud,
              show: this.showAdminStuff,
              items: [
                {
                  title: this.$i18n.t("pages.sushi_monthly_overview"),
                  linkTo: "sushi-monthly-overview",
                  show: this.showAdminStuff,
                },
                {
                  title: this.$i18n.t("pages.sushi_management"),
                  linkTo: "sushi-credentials-list",
                  show: this.showAdminStuff,
                },
                {
                  title: this.$t("pages.sushi_fetch_attempts"),
                  linkTo: "harvests",
                  show: this.showAdminStuff,
                },
                {
                  title: this.$t("pages.sushi_troubleshooting"),
                  linkTo: "sushi-troubleshooting",
                  show: this.showAdminStuff,
                },
                {
                  title: this.$t("pages.sushi_credentials_import"),
                  linkTo: "sushi-credentials-import-list",
                  show: this.showManagementStuff,
                },
              ],
            },
            {
              title: this.$t("pages.manual_data_uploads"),
              icon: this.$icons.upload,
              linkTo: "manual-data-upload-list",
              show: this.showAdminStuff,
            },
            {
              title: this.$t("pages.supported_non_counter_platforms"),
              icon: this.$icons.file_excel,
              linkTo: "supported-non-counter-platforms",
              show: this.showAdminStuff && this.isRawImportEnabled,
            },
            {
              title: this.$t("pages.data_coverage_overview"),
              icon: this.$icons.sitemap,
              linkTo: "data-coverage-overview",
              show: true,
            },
          ],
          show: true,
        },
        {
          title: this.$i18n.t("pages.admin"),
          icon: this.$icons.tools,
          show: this.showManagementStuff,
          items: [
            {
              title: this.$i18n.t("pages.configuration"),
              icon: this.$icons.tools,
              show: this.showManagementStuff,
              items: [
                {
                  title: this.$i18n.t("pages.management"),
                  linkTo: "management",
                  show: this.showManagementStuff,
                },
                {
                  title: this.$t("pages.maintenance"),
                  linkTo: "maintenance",
                  show: this.showManagementStuff,
                },
                {
                  title: this.$t("pages.management_commands"),
                  linkTo: "management-commands",
                  show: this.isSuperuser,
                },
                {
                  title: this.$i18n.t("pages.counter_registry"),
                  linkTo: "counter-registry",
                  show: this.showManagementStuff && this.usesRegistryBackend,
                },
              ],
            },
          ],
        },
        {
          title: this.$i18n.t("pages.organization_administration"),
          icon: this.$icons.tools,
          show: this.allowUserManagement,
          items: [
            {
              title: this.$i18n.t("pages.account_management"),
              icon: this.$icons.user,
              linkTo: "account-management",
              show: this.allowUserManagement,
            },
          ],
        },
        {
          title: this.$i18n.t("pages.whats_new"),
          icon: this.$icons.info,
          show: true,
          items: [
            {
              title: this.$i18n.t("pages.events"),
              icon: this.$icons.email,
              linkTo: "events",
            },
            {
              title: this.$i18n.t("pages.releases"),
              icon: this.$icons.access_point,
              linkTo: "releases",
            },
            {
              title: this.$i18n.t("pages.changelog"),
              icon: this.$icons.list,
              linkTo: "changelog",
            },
          ],
        },
      ];
    },
    activeGroups() {
      return this.groups.filter((group) => group.show);
    },
  },

  watch: {
    show() {
      this.$emit("update:modelValue", this.show);
    },
    modelValue() {
      this.show = this.modelValue;
    },
  },
  mounted() {},
};
</script>

<style scoped>
/* menu item icons sit 36px from the drawer edge (8px list padding + 12px item
   margin + 8px item padding + 8px icon margin) - align the logo and the group
   subheaders with them */
.logo-container {
  padding-left: 36px;
}

.menu-subheader {
  padding-inline-start: 28px; /* 28px + 8px list padding = 36px */
}

img.logo {
  max-width: 128px;
  height: 36px;
}

/* Chrome draws a full-width scrollbar with a light track next to the nav bar;
   make it thin and blend it into the bar like Firefox's overlay scrollbar. */
.v-navigation-drawer :deep(.v-navigation-drawer__content) {
  scrollbar-width: thin;
  scrollbar-color: color-mix(
      in srgb,
      rgb(var(--v-theme-on-nav-bar)) 35%,
      transparent
    )
    transparent;
}

@media only screen and (max-width: 1280px) {
  .v-navigation-drawer {
    height: 100% !important;
  }
  .v-navigation-drawer__scrim {
    height: calc(100% + 50px) !important;
  }
}
</style>
